"use client"

import { useEffect, useState } from "react"
import firebase from "firebase/compat/app"
import "firebase/compat/firestore"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faHistory,
  faUser,
  faCalendarAlt,
  faClock,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faVideo,
} from "@fortawesome/free-solid-svg-icons"
import "./MeetingHistory.css"

export default function MeetingHistory({ userId }) {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [meetingHistory, setMeetingHistory] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(true)

  const handleChangePage = (newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  useEffect(() => {
    console.log("userId type:", typeof userId, "value:", userId)
    if (!userId) {
      console.error("No userId provided to MeetingHistory")
      setLoading(false)
      return
    }

    const userIdString = userId.toString()
    if (!userIdString.trim()) {
      console.error("Invalid userId: userId is empty after conversion")
      setLoading(false)
      return
    }

    const userDoc = firebase.firestore().collection("users").doc(userIdString)
    const meetingsCollection = userDoc.collection("meetingHistory")

    const unsubscribe = meetingsCollection.onSnapshot(
      (snapshot) => {
        const meetingsData = snapshot.docs.map((doc) => {
          const data = doc.data()
          console.log(`Document ${doc.id} data:`, data)
          return {
            id: doc.id,
            ...data,
          }
        })

        // Sort by start time (most recent first)
        meetingsData.sort((a, b) => {
          const aTime =
            a.startTime instanceof firebase.firestore.Timestamp ? a.startTime.toDate() : new Date(a.startTime || 0)
          const bTime =
            b.startTime instanceof firebase.firestore.Timestamp ? b.startTime.toDate() : new Date(b.startTime || 0)
          return bTime - aTime
        })

        setMeetingHistory(meetingsData)
        setLoading(false)
      },
      (error) => {
        console.error("Error fetching meeting history:", error)
        setLoading(false)
      },
    )

    return () => unsubscribe()
  }, [userId])

  // Filter meetings based on search term
  const filteredMeetings = meetingHistory.filter(
    (meeting) =>
      meeting.participantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      meeting.callId?.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const totalPages = Math.ceil(filteredMeetings.length / rowsPerPage)
  const startIndex = page * rowsPerPage
  const endIndex = startIndex + rowsPerPage
  const currentMeetings = filteredMeetings.slice(startIndex, endIndex)

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A"
    try {
      const date = timestamp instanceof firebase.firestore.Timestamp ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    } catch (error) {
      console.error("Error parsing date:", error)
      return "N/A"
    }
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return "N/A"
    try {
      const date = timestamp instanceof firebase.firestore.Timestamp ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    } catch (error) {
      console.error("Error parsing time:", error)
      return "N/A"
    }
  }

  const getStatusBadge = (status) => {
    const statusClasses = {
      ongoing: "status-ongoing",
      ended: "status-ended",
      pending: "status-pending",
    }

    return <span className={`status-badge ${statusClasses[status] || "status-unknown"}`}>{status || "Unknown"}</span>
  }

  if (loading) {
    return (
      <div className="meeting-history-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading meeting history...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="meeting-history-container">
      <div className="meeting-history-header">
        <div className="header-title">
          <FontAwesomeIcon icon={faHistory} className="header-icon" />
          <h2>Meeting History</h2>
        </div>
        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-number">{meetingHistory.length}</span>
            <span className="stat-label">Total Meetings</span>
          </div>
        </div>
      </div>

      <div className="meeting-history-controls">
        <div className="search-container">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search by patient name or call ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="rows-per-page">
          <label>Show:</label>
          <select value={rowsPerPage} onChange={handleChangeRowsPerPage} className="rows-select">
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>entries</span>
        </div>
      </div>

      <div className="meeting-history-content">
        {currentMeetings.length > 0 ? (
          <>
            <div className="meetings-table">
              <div className="table-header">
                <div className="table-cell header-cell">
                  <FontAwesomeIcon icon={faVideo} className="cell-icon" />
                  No.
                </div>
                <div className="table-cell header-cell">
                  <FontAwesomeIcon icon={faUser} className="cell-icon" />
                  Patient Name
                </div>
                <div className="table-cell header-cell">
                  <FontAwesomeIcon icon={faCalendarAlt} className="cell-icon" />
                  Date
                </div>
                <div className="table-cell header-cell">
                  <FontAwesomeIcon icon={faClock} className="cell-icon" />
                  Start Time
                </div>
                <div className="table-cell header-cell">
                  <FontAwesomeIcon icon={faClock} className="cell-icon" />
                  End Time
                </div>
                <div className="table-cell header-cell">Duration</div>
                <div className="table-cell header-cell">Status</div>
              </div>

              <div className="table-body">
                {currentMeetings.map((meeting, index) => (
                  <div key={meeting.id} className="table-row">
                    <div className="table-cell">
                      <span className="row-number">{startIndex + index + 1}</span>
                    </div>
                    <div className="table-cell">
                      <div className="patient-info">
                        <div className="patient-avatar">
                          <FontAwesomeIcon icon={faUser} />
                        </div>
                        <div className="patient-details">
                          <span className="patient-name">{meeting.participantName || "Unknown Patient"}</span>
                          <span className="call-id">ID: {meeting.callId || "N/A"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="table-cell">
                      <span className="date-text">{formatDate(meeting.startTime)}</span>
                    </div>
                    <div className="table-cell">
                      <span className="time-text">{formatTime(meeting.startTime)}</span>
                    </div>
                    <div className="table-cell">
                      <span className="time-text">{formatTime(meeting.endTime)}</span>
                    </div>
                    <div className="table-cell">
                      <span className="duration-text">{meeting.duration || "N/A"}</span>
                    </div>
                    <div className="table-cell">{getStatusBadge(meeting.status)}</div>
                  </div>
                ))}
              </div>
            </div>

            {totalPages > 1 && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredMeetings.length)} of {filteredMeetings.length}{" "}
                  entries
                  {searchTerm && ` (filtered from ${meetingHistory.length} total entries)`}
                </div>
                <div className="pagination-controls">
                  <button className="pagination-btn" onClick={() => handleChangePage(page - 1)} disabled={page === 0}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                    Previous
                  </button>

                  <div className="page-numbers">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum
                      if (totalPages <= 5) {
                        pageNum = i
                      } else if (page < 3) {
                        pageNum = i
                      } else if (page > totalPages - 4) {
                        pageNum = totalPages - 5 + i
                      } else {
                        pageNum = page - 2 + i
                      }

                      return (
                        <button
                          key={pageNum}
                          className={`page-btn ${page === pageNum ? "active" : ""}`}
                          onClick={() => handleChangePage(pageNum)}
                        >
                          {pageNum + 1}
                        </button>
                      )
                    })}
                  </div>

                  <button
                    className="pagination-btn"
                    onClick={() => handleChangePage(page + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    Next
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <FontAwesomeIcon icon={faHistory} />
            </div>
            <h3>No Meeting History</h3>
            <p>
              {searchTerm
                ? `No meetings found matching "${searchTerm}"`
                : "No meetings have been recorded yet. Start your first consultation to see history here."}
            </p>
            {searchTerm && (
              <button className="btn-primary" onClick={() => setSearchTerm("")}>
                Clear Search
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
